import React, { useEffect, useRef, useState } from "react";
import { History, PanelLeftClose, Sparkles } from "lucide-react";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import useCopilot from "../../hooks/useCopilot";

import CopilotComposer from "../../components/copilot/CopilotComposer";
import CopilotSessionList from "../../components/copilot/CopilotSessionList";
import CopilotSuggestions from "../../components/copilot/CopilotSuggestions";
import CopilotThread from "../../components/copilot/CopilotThread";
import { COPILOT_SUGGESTIONS } from "../../components/copilot/constants";

/**
 * Full-page AI Copilot workspace.
 *
 * Layout mirrors the rest of the app: ContainerCard + ModuleTitleCard, with the
 * conversation list in a collapsible left rail and the thread on the right.
 * Answers are scoped to the signed-in employee by the backend — see BE/ai.
 */
const AiCopilot = () => {
    const [showSessions, setShowSessions] = useState(true);

    const {
        sessionId,
        messages,
        sending,
        sessions,
        loadingSessions,
        loadingHistory,
        feedbackState,
        send,
        rate,
        loadSessions,
        openSession,
        startNewChat,
        deleteSession,
    } = useCopilot();

    const employeeName = sessionStorage.getItem("employee_name") || "";
    const firstName = employeeName.split(" ")[0] || "";
    const isHr =
        JSON.parse(sessionStorage.getItem("is_admin") || "false") ||
        JSON.parse(sessionStorage.getItem("is_super_admin") || "false");

    // Load the session list once on mount; aborts if the user navigates away
    // before it resolves (useApiMethods surfaces that as status: "cancelled").
    useEffect(() => {
        const controller = new AbortController();
        loadSessions(controller.signal);
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Restore the conversation the user was already having.
    //
    // The docked launcher (CopilotLauncher) and this page keep separate message
    // state but share a session id through sessionStorage, so arriving here via
    // "expand" would otherwise show an empty thread. Replay it once the session
    // list confirms the id actually has stored messages — a brand-new id from
    // startNewChat() isn't persisted yet and would 400 on the history call.
    const restoredRef = useRef(false);
    useEffect(() => {
        if (restoredRef.current || messages.length || !sessions.length) return;
        const stored = sessions.find((session) => session.session_id === sessionId);
        if (stored?.message_count) {
            restoredRef.current = true;
            openSession(sessionId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessions, sessionId, messages.length]);

    // Refresh the list after the first answer in a new conversation, so the
    // thread appears in the rail without a manual reload.
    useEffect(() => {
        if (messages.length === 2) loadSessions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages.length]);

    const handleNewChat = () => {
        // Block the restore effect: without this, clearing the thread would let
        // it fire again and pull the old conversation straight back in.
        restoredRef.current = true;
        startNewChat();
        loadSessions();
    };

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={Sparkles}
                title="AI Copilot"
                subtitle="Ask about your leave, attendance, payslips, training, holidays and document compliance — answered from live HRMS data."
            />

            <div className="flex h-[calc(100vh-190px)] gap-3 px-3 pb-3">
                {/* Conversation rail */}
                {showSessions && (
                    <aside className="hidden w-64 shrink-0 rounded-lg border border-border bg-card lg:block">
                        <CopilotSessionList
                            sessions={sessions}
                            activeSessionId={sessionId}
                            loading={loadingSessions}
                            onSelect={openSession}
                            onNewChat={handleNewChat}
                            onDelete={deleteSession}
                        />
                    </aside>
                )}

                {/* Chat column */}
                <section className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card">
                    <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
                        <div className="flex min-w-0 items-center gap-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowSessions((prev) => !prev)}
                                title={showSessions ? "Hide conversations" : "Show conversations"}
                                className="hidden h-8 w-8 p-0 text-muted-foreground hover:text-sidebar-primary lg:inline-flex"
                            >
                                {showSessions ? (
                                    <PanelLeftClose className="h-4 w-4" />
                                ) : (
                                    <History className="h-4 w-4" />
                                )}
                            </Button>
                            <p className="truncate text-sm font-medium text-foreground">
                                {messages.length ? "Current conversation" : "New conversation"}
                            </p>
                        </div>

                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleNewChat}
                            className="h-8 text-xs"
                        >
                            New chat
                        </Button>
                    </div>

                    <CopilotThread
                        messages={messages}
                        sending={sending}
                        loading={loadingHistory}
                        userName={firstName}
                        feedbackState={feedbackState}
                        onRate={rate}
                    />

                    <div
                        className={cn(
                            "space-y-2 border-t border-border bg-background p-3"
                        )}
                    >
                        {!messages.length && (
                            <CopilotSuggestions
                                items={COPILOT_SUGGESTIONS}
                                onPick={send}
                                disabled={sending}
                                isHr={isHr}
                            />
                        )}
                        <CopilotComposer onSend={send} disabled={sending} autoFocus />
                        <p className="text-[10px] text-muted-foreground">
                            The copilot reads your HRMS records to answer. It cannot apply leave,
                            edit records or approve requests.
                        </p>
                    </div>
                </section>
            </div>
        </ContainerCard>
    );
};

export default AiCopilot;
